import React, { useCallback, useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertTriangle, Trash2, Database, RefreshCw, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { LoadError, errorMessage } from '../components/LoadError';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FreshStartCard = ({ country, onDone }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/data/fresh-start`, { confirm: text, country }, { withCredentials: true });
      toast.success('Fresh start complete', { description: res.data.message });
      setText('');
      onDone();
    } catch (err) {
      toast.error('Fresh start failed', { description: err.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-red-300" data-testid="fresh-start-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2 text-red-700">
          <AlertTriangle className="h-5 w-5" />
          Fresh Start (delete everything incl. SKUs)
        </CardTitle>
        <CardDescription>
          Deletes SKUs, SKU mappings, inventory, sales, targets, POs, shipments, returns and alerts for {country}.
          Users and system settings are kept. Then upload your SKU master files again.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-3">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type RESET to confirm" className="w-56" data-testid="fresh-start-input" />
        <Button variant="destructive" onClick={run} disabled={busy || text !== 'RESET'} data-testid="fresh-start-button">
          <Trash2 className="h-4 w-4 mr-2" />{busy ? 'Deleting…' : 'Delete everything'}
        </Button>
      </CardContent>
    </Card>
  );
};

export const SettingsPage = () => {
  const { user } = useAuth();
  const { country } = useCountry();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [clearAllOpen, setClearAllOpen] = useState(false);
  const [clearKind, setClearKind] = useState('sales'); // which single-type clear the dialog is for
  const [clearOpen, setClearOpen] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setStatsError(null);
    try {
      const response = await axios.get(`${API_URL}/api/data/stats?country=${country}`, { withCredentials: true });
      setStats(response.data);
    } catch (err) {
      setStatsError(err);
    } finally {
      setLoading(false);
    }
  }, [country]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Called from the ConfirmDialog; rethrows so the dialog stays open on failure.
  const handleClearAllData = async () => {
    setClearing(true);
    try {
      const response = await axios.delete(`${API_URL}/api/data/clear-all-transactions?country=${country}`, { withCredentials: true });
      toast.success('Data Cleared Successfully', { description: response.data.message });
      fetchStats();
    } catch (err) {
      toast.error('Failed to clear data', { description: errorMessage(err) });
      throw err;
    } finally {
      setClearing(false);
    }
  };

  // Called from the ConfirmDialog; rethrows so the dialog stays open on failure.
  const handleClearSales = async () => {
    setClearing(true);
    try {
      const response = await axios.delete(`${API_URL}/api/data/clear-sales?country=${country}`, { withCredentials: true });
      toast.success('Sales Data Cleared', { description: response.data.message });
      fetchStats();
    } catch (err) {
      toast.error('Failed to clear sales data', { description: errorMessage(err) });
      throw err;
    } finally {
      setClearing(false);
    }
  };

  const handleClearInventory = async () => {
    setClearing(true);
    try {
      const response = await axios.delete(`${API_URL}/api/data/clear-inventory?country=${country}`, { withCredentials: true });
      toast.success('Inventory Data Cleared', { description: response.data.message });
      fetchStats();
    } catch (err) {
      toast.error('Failed to clear inventory data', { description: errorMessage(err) });
      throw err;
    } finally {
      setClearing(false);
    }
  };

  const clearCount = (n) => (typeof n === 'number' ? `${n.toLocaleString('en-IN')} ` : 'all ');
  const clearConfig = clearKind === 'sales'
    ? {
        title: `Delete ${clearCount(stats?.sales)}sales records for ${country}?`,
        description: `Every daily sales record for ${country} will be permanently deleted. SKUs, inventory and other countries are not affected. This cannot be undone.`,
        confirmLabel: 'Delete sales data',
        onConfirm: handleClearSales,
      }
    : {
        title: `Delete ${clearCount(stats?.inventory)}inventory records for ${country}?`,
        description: `Every inventory record for ${country} will be permanently deleted. SKUs, sales and other countries are not affected. This cannot be undone.`,
        confirmLabel: 'Delete inventory data',
        onConfirm: handleClearInventory,
      };

  const clearAllCount = stats
    ? ['sales', 'inventory', 'retail_pos', 'shipments', 'alerts']
        .reduce((sum, key) => sum + (typeof stats[key] === 'number' ? stats[key] : 0), 0)
    : null;

  if (user?.role !== 'admin') {
    return (
      <div>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-red-600">
              <XCircle className="h-5 w-5" />
              <span>Admin access required to view settings</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Data Management</h1>
        <p className="text-gray-500 text-sm mt-1">Manage application data and configuration</p>
      </div>

      {/* Data Statistics */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Database className="h-5 w-5 text-blue-500" />
                Database Statistics
              </CardTitle>
              <CardDescription>Current data in your database</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchStats} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading && !stats && (
            <div role="status" className="text-center py-4 text-gray-500">Loading…</div>
          )}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-gray-900">{stats.skus}</div>
                <div className="text-sm text-gray-500">SKUs</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-blue-600">{stats.sales}</div>
                <div className="text-sm text-gray-500">Sales Records</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-green-600">{stats.inventory}</div>
                <div className="text-sm text-gray-500">Inventory Records</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-purple-600">{stats.retail_pos}</div>
                <div className="text-sm text-gray-500">Retail POs</div>
              </div>
              <div className="bg-orange-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-orange-600">{stats.shipments}</div>
                <div className="text-sm text-gray-500">Shipments</div>
              </div>
              <div className="bg-red-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-red-600">{stats.alerts}</div>
                <div className="text-sm text-gray-500">Alerts</div>
              </div>
              <div className="bg-teal-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-teal-600">{stats.targets}</div>
                <div className="text-sm text-gray-500">Targets</div>
              </div>
              <div className="bg-indigo-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-indigo-600">{stats.users}</div>
                <div className="text-sm text-gray-500">Users</div>
              </div>
            </div>
          )}
          {!loading && !stats && statsError && (
            <LoadError what="the database statistics" error={statsError} onRetry={fetchStats} retrying={loading} testId="settings-stats-error" />
          )}
        </CardContent>
      </Card>

      {/* Clear Individual Data */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Clear Individual Data</CardTitle>
          <CardDescription>Permanently delete one data type for {country} while keeping others intact</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button 
              variant="outline" 
              onClick={() => { setClearKind('sales'); setClearOpen(true); }}
              disabled={clearing}
              className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
              data-testid="clear-sales-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear Sales Data
            </Button>
            <Button 
              variant="outline" 
              onClick={() => { setClearKind('inventory'); setClearOpen(true); }}
              disabled={clearing}
              className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
              data-testid="clear-inventory-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear Inventory Data
            </Button>
          </div>
          <ConfirmDialog
            open={clearOpen}
            onOpenChange={setClearOpen}
            title={clearConfig.title}
            description={clearConfig.description}
            confirmLabel={clearConfig.confirmLabel}
            busyLabel="Deleting…"
            destructive
            typeToConfirm={country}
            onConfirm={clearConfig.onConfirm}
            testId={`clear-${clearKind}-confirm`}
          />
        </CardContent>
      </Card>

      {/* Clear All Transactional Data */}
      <Card className="border-red-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Danger Zone
          </CardTitle>
          <CardDescription>
            Clear all transactional data to start fresh. This will delete sales, inventory, POs, shipments, and alerts.
            <br />
            <strong>SKUs, Users, and Targets will be preserved.</strong>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-red-700">
              <strong>Warning:</strong> This action cannot be undone. All transactional data will be permanently deleted.
              After clearing, you can upload fresh data from the Sales records and Inventory pages.
            </p>
          </div>
          
          <Button
            variant="destructive"
            onClick={() => setClearAllOpen(true)}
            disabled={clearing}
            data-testid="clear-all-data-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clear All Transactional Data
          </Button>
          <ConfirmDialog
            open={clearAllOpen}
            onOpenChange={setClearAllOpen}
            title={`Delete ${clearCount(clearAllCount)}transactional records for ${country}?`}
            description={`All sales, inventory, stock issues, B2B orders with their invoices and payments, retail POs, shipments and alerts for ${country} will be permanently deleted. SKUs, users, targets and other countries are not affected. This cannot be undone.`}
            confirmLabel="Clear all data"
            busyLabel="Clearing…"
            destructive
            typeToConfirm={country}
            onConfirm={handleClearAllData}
            testId="confirm-clear-all"
          >
            {stats && (
              <ul className="text-[13px] text-gray-600 space-y-0.5" data-testid="confirm-clear-all-counts">
                <li>Sales records: {Number(stats.sales || 0).toLocaleString('en-IN')}</li>
                <li>Inventory records: {Number(stats.inventory || 0).toLocaleString('en-IN')}</li>
                <li>B2B orders: {Number(stats.b2b_orders || 0).toLocaleString('en-IN')}</li>
                <li>Retail POs: {Number(stats.retail_pos || 0).toLocaleString('en-IN')}</li>
                <li>Shipments: {Number(stats.shipments || 0).toLocaleString('en-IN')}</li>
                <li>Alerts: {Number(stats.alerts || 0).toLocaleString('en-IN')}</li>
              </ul>
            )}
          </ConfirmDialog>
        </CardContent>
      </Card>

      <FreshStartCard country={country} onDone={fetchStats} />

      {/* Instructions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">After Clearing Data</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
            <li>Go to <Link to="/daily-sales" className="font-semibold text-blue-600 hover:underline">Sales records</Link> and click <strong>Upload sales</strong> to upload your sales Excel file</li>
            <li>Go to <Link to="/inventory" className="font-semibold text-blue-600 hover:underline">Inventory</Link> and click <strong>Bulk Update</strong> to upload your inventory Excel file</li>
            <li>Optionally, record Purchases and import Retail POs using their respective import features</li>
            <li>Dashboard and Performance metrics will automatically update based on the imported data</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
